import {
  PermissionBit,
  QuickLookTasks,
  SeerlinqComputedWithPPGResponse,
} from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { PPGQuickLook } from "../../../models/quick-look-analyses";

export type ComputedDataItem = SeerlinqComputedWithPPGResponse & {
  ppgConditions?: string[];
  ppgQuickLook?: PPGQuickLook | null;
  ppgDevice?: string;
};

export class ComputedData extends DataModel<ComputedDataItem> {
  private quickLookByUuid = new Map<string, PPGQuickLook>();

  constructor(
    data: ComputedDataItem[],
    permissions: PermissionBit[],
    userUUID: string,
    private api: ApiConnector | null = null,
    private qlAnalysisType: QuickLookTasks | null = null,
  ) {
    super(data, permissions, userUUID);

    // access
    this.access = {
      editRules: {
        medical: AccessRules.visible,
        monitoring: AccessRules.visible,
      },
      deleteRules: { medical: AccessRules.none, monitoring: AccessRules.none },
      historyRequiresMonitoring: false,
    };
  }

  init() {
    const activeQuickLookUuids = new Set<string>();

    this.data = this.data.map((item: SeerlinqComputedWithPPGResponse) => {
      const ppgs = item.ppgs ?? [];
      const quickLookUuids = ppgs.map((ppg) => ppg.uuid);
      const rowUuid = String(item.uuid);
      const ppgQuickLook =
        this.api !== null && this.qlAnalysisType !== null
          ? (this.quickLookByUuid.get(rowUuid) ??
            new PPGQuickLook(this.api, this.qlAnalysisType, quickLookUuids))
          : null;

      if (ppgQuickLook) {
        activeQuickLookUuids.add(rowUuid);
        ppgQuickLook.syncSelection(this.qlAnalysisType, quickLookUuids);
        this.quickLookByUuid.set(rowUuid, ppgQuickLook);
      }

      const ppgConditions = ppgs.map((ppg) => ppg.measurement_condition);
      const ppgDevice = [
        ...new Set(ppgs.map((ppg) => ppg.measurement_device)),
      ][0];
      return {
        ...item,
        ppgConditions: ppgConditions.sort((a, b) => a.localeCompare(b)),
        ppgQuickLook: ppgQuickLook,
        ppgDevice: ppgDevice,
      };
    });

    for (const uuid of this.quickLookByUuid.keys()) {
      if (!activeQuickLookUuids.has(uuid)) {
        this.quickLookByUuid.delete(uuid);
      }
    }

    super.init();
  }
}
